import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useContext,
    useEffect,
    useRef,
    useState,
    useMemo,
    useCallback,
} from 'react';

import { createTheme } from '@mui/material';
import { endOfDay, endOfMonth, startOfDay, startOfMinute, startOfMonth, subDays } from 'date-fns';
import i18next from 'i18next';
import merge from 'lodash/merge';

import { AppContextState, EventDateSummary, Filter, FilterType, Layouts } from './types';
import { useA1, DEFAULT_LIMIT } from 'api';
import { QueryParams } from 'api/useA1';
import { CONTENT_VIEW } from 'constants/content';
import { FILTER_BY } from 'constants/filter';
import { SortDirection, SORT_DIRECTION } from 'constants/sort';
import { defaultLanguage, i18nInit } from 'i18n';
import { A1Calendar, A1Events, A1Filters } from 'interfaces/A1Results';
import { CalendarView } from 'interfaces/ICalendar';
import { IEvent } from 'interfaces/IEvent';
import { createCustomLocalizationUrl } from 'services/createUrl';
import { eventillaDate } from 'services/format';
import loadCustom from 'services/loadCustom';
import { themeEmbed } from 'theme';

const contextInitialValues: AppContextState = {
    events: [],
    contentView: CONTENT_VIEW.List,
    contentViews: [CONTENT_VIEW.List, CONTENT_VIEW.Grid, CONTENT_VIEW.Calendar],
    layouts: {
        [CONTENT_VIEW.List]: {
            filters: ['LOCATION', 'SEARCH', 'TAGS'],
            eventFields: ['NAME', 'LOCATION', 'DATE', 'REGISTRATION_BUTTON', 'LOGO'],
        },
        [CONTENT_VIEW.Grid]: {
            filters: ['LOCATION', 'SEARCH', 'TAGS'],
            eventFields: ['DESCRIPTION', 'NAME', 'LOCATION', 'DATE', 'REGISTRATION_BUTTON', 'LOGO'],
            logoHeight: 200,
            logoHeightAuto: false,
        },
        [CONTENT_VIEW.Calendar]: {
            filters: ['LOCATION', 'SEARCH', 'TAGS'],
            eventFields: ['NAME', 'LOCATION', 'DATE', 'REGISTRATION_BUTTON', 'SLOTS', 'TICKETS'],
        },
    },
    setContentView: () => {
        console.error('No provider');
    },
    direction: SORT_DIRECTION.Ascending,
    setDirection: () => {
        console.error('No provider');
    },
    filterType: {
        [Filter.FilterBy]: FILTER_BY.UpcomingEvents,
        [Filter.DateRange]: [null, null],
        [Filter.Locations]: new Set(),
        [Filter.Tags]: [],
        [Filter.TextField]: null,
    },
    setFilterType: () => {
        console.error('No provider');
    },
    setLanguage: () => {
        console.error('No provider');
    },
    setEventPage: () => {
        console.error('No provider');
    },
    foundTags: new Set(), // Tags found through events/ api calls
    availableLocations: new Set(),
    datetimeFormat: 'DD MMM YYYY hh:mm',
    eventsDefaultImage: null,
    loading: true,
    eventLoading: false,
    error: null,
    changedLanguage: null,
    translations: [],
    organizationUri: null,
    theme: themeEmbed, //theme,
    hasMoreEvents: false,
    directUrl: false,
    organizationDefaultCalendarLogo: '',
    eventDates: [],
    selectedDate: new Date(),
    setSelectedDate: () => {
        console.error('No provider');
    },
};

export const AppContext = createContext<AppContextState>(contextInitialValues);
export const useApp = (): AppContextState => useContext(AppContext);

const isDateValid = (dateStr: string) => {
    return !isNaN(new Date(dateStr).getTime());
};

const AppProvider: FC = ({ children }: any) => {
    const [initializingCalendar, setInitializingCalendar] = useState(true);
    const [initializingLocalizations, setInitializingLocalizations] = useState(true);
    const [events, setEvents] = useState<IEvent[]>(contextInitialValues.events);
    const [contentView, setContentView] = useState<CalendarView>(contextInitialValues.contentView);
    const [contentViews, setContentViews] = useState<CalendarView[]>(contextInitialValues.contentViews);
    const [layouts, setLayouts] = useState<Layouts>(contextInitialValues.layouts);
    const [direction, setDirection] = useState<SortDirection>(contextInitialValues.direction);
    const [filterType, setFilterType] = useState<FilterType>(() => {
        const initialFilterType = { ...contextInitialValues.filterType };
        const searchParams = new URLSearchParams(window.location.search);
        const searchParam = searchParams.get('search');
        if (searchParam) {
            const withoutHtmlTags = searchParam.replace(/<[^>]*>/g, '');
            initialFilterType.textField = withoutHtmlTags.trim();
        }
        return initialFilterType;
    });
    const [datetimeFormat, setDatetimeFormat] = useState(contextInitialValues.datetimeFormat);
    const [eventsDefaultImage, setEventsDefaultImage] = useState(contextInitialValues.eventsDefaultImage);
    const [organizationDefaultCalendarLogo, setOrganizationDefaultCalendarLogo] = useState(
        contextInitialValues.organizationDefaultCalendarLogo
    );
    const [changedLanguage, setLanguage] = useState(null);
    const [isStaticTagList, setIsStaticTagList] = useState(false);
    const [staticLocationList, setStaticLocationList] = useState(false);
    const [theme, setTheme] = useState(null);
    const [customLocalizations, setCustomLocalizations] = useState(null);
    const [directUrl, setDirectUrl] = useState(contextInitialValues.directUrl);
    const [eventDates, setEventDates] = useState<EventDateSummary[]>(contextInitialValues.eventDates);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());

    const [embedCalendarId] = useState<string | null>(() => {
        // Embedded id from backend script embed-calendar.js
        let embedCalendarId = window?.calendar_id;
        if (!embedCalendarId) {
            // Secondary way to embed id
            // Use calendar url param for embedding ?calendar=uuid
            const urlParams = new URLSearchParams(window.location.search);

            // URLSearchParams get returns null if param not set
            embedCalendarId = urlParams.get('calendar');
        }
        return embedCalendarId;
    });
    const isEmbedded = !!embedCalendarId;
    const embedLanguage = window?.calendar_language;
    // Get calendar
    // Use embedCalendarId to determine hardcoded rest url
    // TS bug? embedCalendarId can be null here, but TS shows only string type
    const calendarId = embedCalendarId ?? window.location.pathname.split('/')?.[2];
    const { result: calendarResult, error }: A1Calendar = useA1('calendars/' + calendarId, {
        skip: calendarId === null,
        embedUrl: isEmbedded,
    });

    const calendar = calendarResult?.data;
    const foundTags = contextInitialValues.foundTags;
    const availableLocations = contextInitialValues.availableLocations;

    // Create events query params from calendar filters
    const calendarEventFilters: QueryParams = {};
    const urlParams = new URLSearchParams(window.location.search);

    if (filterType.textField && filterType.textField !== '') {
        calendarEventFilters.search = filterType.textField;
    }
    if (filterType.tags.length > 0) {
        calendarEventFilters.tags = [];
        Array.from(foundTags).forEach((tag) => {
            if (
                filterType.tags.includes(
                    (tag.display_name !== null && tag.display_name !== '' ? tag.display_name : tag.key).toLowerCase()
                )
            ) {
                calendarEventFilters.tags.push(String(tag.id));
            }
        });
    }

    if (urlParams.size > 0) {
        const eventTags = urlParams.get('event_tag');
        if (eventTags) {
            const formattedTags = eventTags.split(',');
            filterType.tags = formattedTags;
        }

        const eventCity = urlParams.get('event_city');
        if (eventCity) {
            const formattedCity = eventCity.split(',');
            formattedCity.forEach((city) => {
                filterType.locations.add(city);
            });
        }

        const eventStartDate = urlParams.get('event_start_date');
        if (eventStartDate && isDateValid(eventStartDate)) {
            filterType.dateRange[0] = new Date(eventStartDate);
            filterType.filterBy = 'date-range';
        }

        const eventEndDate = urlParams.get('event_end_date');
        if (eventEndDate && isDateValid(eventEndDate)) {
            filterType.dateRange[1] = new Date(eventEndDate);
            filterType.filterBy = 'date-range';
        }
    }

    if (filterType.locations.size > 0) {
        calendarEventFilters.location = Array.from(filterType.locations);
    }
    // Separate from/to handling for calendar view
    if (contentView === CONTENT_VIEW.Calendar) {
        calendarEventFilters.view_type = 'calendar';
        // Date range comes from changing visible month. By default we show current date events
        if (filterType.dateRange[0] !== null && filterType.dateRange[1] !== null) {
            calendarEventFilters.from = eventillaDate(filterType.dateRange[0]);
            calendarEventFilters.to = eventillaDate(filterType.dateRange[1]);
        } else {
            calendarEventFilters.from = eventillaDate(startOfMonth(new Date()));
            calendarEventFilters.to = eventillaDate(endOfMonth(new Date()));
        }
    } else {
        calendarEventFilters.direction = direction;
        calendarEventFilters.sort_by = 'starts';
        if (filterType.filterBy === FILTER_BY.AllEvents && calendar?.event_filters.past_in_days) {
            const from = eventillaDate(
                startOfDay(subDays(new Date(), Number(calendar?.event_filters.past_in_days ?? 0)))
            );
            calendarEventFilters.from = from;
            calendarEventFilters.to = null;
        } else if (filterType.filterBy === FILTER_BY.UpcomingEvents) {
            calendarEventFilters.from = eventillaDate(startOfMinute(new Date()));
            calendarEventFilters.to = null;
        } else if (filterType.filterBy === FILTER_BY.DateRange && filterType.dateRange.length === 2) {
            calendarEventFilters.from = filterType.dateRange[0]
                ? eventillaDate(startOfDay(filterType.dateRange[0]))
                : null;
            calendarEventFilters.to = filterType.dateRange[1] ? eventillaDate(endOfDay(filterType.dateRange[1])) : null;
        }
    }
    const { result: filterResults }: A1Filters = useA1('calendars/' + calendarId + '/filter_resources', {
        query: {
            language: embedLanguage?.toUpperCase() || (i18next?.language?.toUpperCase() ?? 'EN'),
            ...calendarEventFilters,
        },
        skip: initializingCalendar || initializingLocalizations, // Calendar must be loaded before we load events
        pagination: contentView !== CONTENT_VIEW.Calendar,
        embedUrl: !!embedCalendarId,
    });
    // Get events
    // get the limit from url query params
    const queryParams = new URLSearchParams(window.location.search);
    const limitParam = queryParams.get('limit');
    const calendarLimit = calendar?.layout?.events_per_page;
    const limit =
        limitParam && !isNaN(Number(limitParam)) && Number(limitParam) > 0
            ? Number(limitParam)
            : calendarLimit && calendarLimit > 0
                ? calendarLimit
                : DEFAULT_LIMIT;
    const {
        result: eventResult,
        setPage: setEventPage,
        loading: eventLoading,
    }: A1Events = useA1('calendars/' + calendarId + '/events', {
        query: {
            limit: contentView === CONTENT_VIEW.Calendar ? 1000 : limit, // Calendar limit overrides default limit
            organization_uuid: calendar?.organization_uuid,
            suborganizations: true,
            language: embedLanguage?.toUpperCase() || (i18next?.language?.toUpperCase() ?? 'EN'),
            ...calendarEventFilters,
        },
        skip: initializingCalendar || initializingLocalizations, // Calendar must be loaded before we load events
        pagination: contentView !== CONTENT_VIEW.Calendar,
        embedUrl: !!embedCalendarId,
    });

    const calendarSummary = eventDates.length > 0;

    const { result: dayResult } = useA1('calendars/' + calendarId + '/events', {
        query: {
            limit: 100, // Day limit overrides default limit
            from: eventillaDate(startOfDay(selectedDate)),
            to: eventillaDate(endOfDay(selectedDate)),
            organization_uuid: calendar?.organization_uuid,
            suborganizations: true,
            language: embedLanguage?.toUpperCase() || (i18next?.language?.toUpperCase() ?? 'EN'),
            ...(calendarEventFilters.search ? { search: calendarEventFilters.search } : {}),
            ...(calendarEventFilters.tags ? { tags: calendarEventFilters.tags } : {}),
            ...(calendarEventFilters.location ? { location: calendarEventFilters.location } : {}),
        },
        skip: initializingCalendar || initializingLocalizations || !calendarSummary,
        embedUrl: !!embedCalendarId,
    });

    useEffect(() => {
        if (calendarSummary && dayResult) {
            setEvents(dayResult.data ?? []);
        }
    }, [dayResult, calendarSummary]);

    const handleSetFilterType: Dispatch<SetStateAction<FilterType>> = useCallback(
        (newFilterType) => {
            setFilterType(newFilterType);
            setEventPage(0);
        },
        [setFilterType, setEventPage]
    );

    // Track last processed eventResult to prevent duplicate appending when only contentView changes
    const lastProcessedEventResult = useRef<typeof eventResult>(null);

    useEffect(() => {
        if (eventResult?.data) {
            if (filterResults?.data) {
                filterResults.data.cities.forEach((city) => {
                    if (!staticLocationList) {
                        // Ignore all already available locations, ignored locations and internal locations
                        const fullIgnoredLocations = [
                            ...calendar.layout.locations.ignored,
                            ...calendar.event_filters.locations,
                            ...Array.from(availableLocations),
                        ].map((city) => city.toLowerCase());
                        if (!fullIgnoredLocations.includes(city)) {
                            availableLocations.add(city);
                        }
                    }
                });

                // Ignore all already found tags, ignored tags and internal tags
                const fullIgnoredTags = [
                    ...calendar.layout.tags.ignored.map((tag) =>
                        (tag.key !== null && tag.key !== '' ? tag.key : '').toLowerCase()
                    ),
                    ...calendar.event_filters.tags.map((tag) =>
                        (tag.key !== null && tag.key !== '' ? tag.key : '').toLowerCase()
                    ),
                    ...Array.from(foundTags).map((tag) =>
                        (tag.key !== null && tag.key !== '' ? tag.key : '').toLowerCase()
                    ),
                ];
                filterResults.data.tags?.forEach((tag) => {
                    if (!fullIgnoredTags.includes((tag.key !== null && tag.key !== '' ? tag.key : '').toLowerCase())) {
                        foundTags.add(tag);
                    } else {
                        foundTags.forEach((existingTag) => {
                            if (existingTag.key === tag.key && existingTag.display_name !== tag.display_name) {
                                foundTags.delete(existingTag);
                                foundTags.add(tag);
                            }
                        });
                    }
                });
            }

            // Skip processing if we've already processed this exact eventResult
            // This prevents duplicate appending when only contentView changes
            if (lastProcessedEventResult.current === eventResult) {
                return;
            }
            lastProcessedEventResult.current = eventResult;

            if (eventResult.event_dates?.length) {
                setEventDates(eventResult.event_dates);
                setEvents([]);
                return;
            }
            setEventDates([]);

            // All but calendar view grows visible event lists
            // Calendar will replace events with new result
            // When starting a new search (first page/offset 0), we also replace all events
            if (contentView === CONTENT_VIEW.Calendar || eventResult.offset === 0) {
                setEvents(eventResult.data);
            } else {
                setEvents((prevEvents) => [...prevEvents, ...eventResult.data]);
            }
        }
    }, [eventResult, filterResults, contentView, isStaticTagList]);

    // Effect to initialize application from calendar settings
    useEffect(() => {
        let cancel = false;
        if (calendar) {
            // Batch all state updates to minimize re-renders
            const updates = {
                datetimeFormat: calendar.datetime_format,
                contentView:
                    calendar.layout.enabled_views.length > 0
                        ? calendar.layout.enabled_views.includes(calendar.layout.default_view)
                            ? calendar.layout.default_view
                            : calendar.layout.enabled_views[0]
                        : contentView,
                contentViews: calendar.layout.enabled_views,
                layouts: {
                    [CONTENT_VIEW.List]: {
                        eventFields: calendar.layout.list_view.event_fields,
                        filters: calendar.layout.list_view.filters,
                    },
                    [CONTENT_VIEW.Grid]: {
                        eventFields: calendar.layout.grid_view.event_fields,
                        filters: calendar.layout.grid_view.filters,
                        logoHeight: calendar.layout.grid_view.logo_height,
                        logoHeightAuto: calendar.layout.grid_view.logo_height_auto,
                    },
                    [CONTENT_VIEW.Calendar]: {
                        eventFields: calendar.layout.calendar_view.event_fields,
                        filters: calendar.layout.calendar_view.filters,
                    },
                },
                directUrl: calendar.event_filters.direct_url,
                eventsDefaultImage: calendar.events_default_image || eventsDefaultImage,
                organizationDefaultCalendarLogo:
                    calendar.organization_default_calendar_logo || organizationDefaultCalendarLogo,
                direction: calendar.event_filters.direction,
                isStaticTagList: calendar.layout.tags.static_list.length > 0,
                staticLocationList: calendar.layout.locations.static_list.length > 0,
            };

            // Apply all updates
            setDatetimeFormat(updates.datetimeFormat);
            setContentView(updates.contentView);
            setContentViews(updates.contentViews);
            setLayouts(updates.layouts);
            setDirectUrl(updates.directUrl);
            setEventsDefaultImage(updates.eventsDefaultImage);
            setOrganizationDefaultCalendarLogo(updates.organizationDefaultCalendarLogo);
            setDirection(updates.direction);
            setIsStaticTagList(updates.isStaticTagList);
            setStaticLocationList(updates.staticLocationList);

            // Handle tags and locations
            if (calendar.layout.tags.static_list.length > 0) {
                calendar.layout.tags.static_list.forEach((tag) => foundTags.add(tag));
            }

            if (calendar.layout.locations.static_list.length > 0) {
                calendar.layout.locations.static_list.forEach((location) => availableLocations.add(location));
            }

            // Handle filter types
            const newFilterTypes: Partial<FilterType> = {};

            if (calendar.layout.tags.selected) {
                let newTags = calendar.layout.tags.selected;
                if (calendar.layout.tags.static_list.length > 0) {
                    newTags = newTags.filter((tag) =>
                        calendar.layout.tags.static_list.some((taglistTag) => taglistTag.key === tag.key)
                    );
                } else {
                    newTags.forEach((tag) => foundTags.add(tag));
                }
                newFilterTypes.tags = newTags.map((tag) =>
                    (tag.display_name !== null && tag.display_name !== '' ? tag.display_name : tag.key).toLowerCase()
                );
            }

            if (calendar.layout.locations.selected) {
                let newLocations = calendar.layout.locations.selected;
                if (calendar.layout.locations.static_list.length > 0) {
                    newLocations = newLocations.filter((location) =>
                        calendar.layout.locations.static_list.includes(location)
                    );
                } else {
                    newLocations.forEach((location) => availableLocations.add(location));
                }
                newFilterTypes.locations = new Set(newLocations);
            }

            if (calendar.layout.events_period) {
                newFilterTypes.filterBy = calendar.layout.events_period.toLowerCase();
            }

            if (Object.keys(newFilterTypes).length > 0) {
                setFilterType((prev) => ({ ...prev, ...newFilterTypes }));
            }

            // Handle theme
            if (calendar.theme !== null) {
                try {
                    const _calendarTheme = JSON.parse(calendar.theme);
                    setTheme(createTheme(merge(themeEmbed, _calendarTheme)));
                } catch {
                    setTheme(createTheme(themeEmbed));
                    console.error('Calendar theme invalid');
                }
            } else {
                setTheme(createTheme(themeEmbed));
            }

            // Handle custom localizations
            const customLocalizationsUri = calendar?.organization_uri
                ? createCustomLocalizationUrl(calendar.organization_uri, isEmbedded)
                : null;
            if (customLocalizationsUri !== null) {
                loadCustom(customLocalizationsUri)
                    .then((json) => {
                        if (!cancel) {
                            setCustomLocalizations(json);
                        }
                    })
                    .catch(() => {
                        setCustomLocalizations({});
                    });
            } else {
                setCustomLocalizations({});
            }

            // Calendar initialization done
            setInitializingCalendar(false);
        }

        return () => {
            cancel = true;
        };
    }, [calendar]);

    // Initialize i18n separately to prevent react hook queue problems
    useEffect(() => {
        if (customLocalizations !== null && calendar) {
            const queryParams = new URLSearchParams(window.location.search);
            let paramLang = queryParams.get('lang');
            if (paramLang) {
                paramLang = paramLang.replace(/[^A-Za-z]/g, '').toUpperCase();
            }

            let defaultCalendarLanguage = defaultLanguage;
            if (calendar?.languages.length > 0) {
                defaultCalendarLanguage = calendar.languages[0].toLowerCase();
            }
            if (paramLang) {
                defaultCalendarLanguage = paramLang.toLowerCase();
            }
            const documentLanguage = embedLanguage?.toLowerCase() ?? defaultCalendarLanguage;
            document.documentElement.lang = documentLanguage;

            i18nInit(
                {
                    customLocalizations: customLocalizations,
                    lng: documentLanguage,
                },
                () => {
                    setInitializingLocalizations(false);
                }
            );
        }
    }, [customLocalizations, calendar, embedLanguage]);

    const contextValue = useMemo(
        () => ({
            events,
            contentView,
            contentViews,
            setContentView,
            direction,
            setDirection,
            filterType,
            changedLanguage,
            setLanguage,
            setFilterType: handleSetFilterType,
            setEventPage,
            availableLocations,
            foundTags,
            datetimeFormat,
            directUrl,
            eventsDefaultImage,
            organizationDefaultCalendarLogo,
            layouts,
            translations: calendarResult?.data.translations ?? [],
            loading: initializingCalendar || initializingLocalizations,
            eventLoading: eventLoading,
            error: calendarId === null ? true : error,
            organizationUri: calendarResult?.data.organization_uri,
            theme: theme,
            hasMoreEvents: eventResult ? eventResult.offset + eventResult.limit < eventResult.total : false,
            eventDates,
            selectedDate,
            setSelectedDate,
        }),
        [
            events,
            contentView,
            contentViews,
            setContentView,
            direction,
            setDirection,
            filterType,
            changedLanguage,
            setLanguage,
            handleSetFilterType,
            setEventPage,
            availableLocations,
            foundTags,
            datetimeFormat,
            directUrl,
            eventsDefaultImage,
            organizationDefaultCalendarLogo,
            layouts,
            calendarResult?.data.translations,
            initializingCalendar,
            initializingLocalizations,
            eventLoading,
            error,
            calendarResult?.data.organization_uri,
            theme,
            eventResult,
            eventDates,
            selectedDate,
        ]
    );

    return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export default AppProvider;
